import { ApolloClient, ApolloProvider, NormalizedCacheObject } from "@apollo/client";
import { usePostHog } from "posthog-js/react";
import React, { Suspense, useEffect, useMemo, useRef } from "react";
import { createNetworkStatusNotifier } from "react-apollo-network-status";
import { useTranslation } from "react-i18next";
import { Redirect, Route, BrowserRouter as Router, Switch, useHistory, useLocation } from "react-router-dom";
import useVH from "react-viewport-height";
import { QueryParamProvider } from "use-query-params";

import { ConnectionLost } from "components/ConnectionLost/ConnectionLost";
import { ConvertToAppModal } from "components/ConvertToAppModal";
import { DeleteJourneyModal } from "components/DeleteJourneyModal";
import { DuplicateJourneyModal } from "components/DuplicateJourneyModal";
import { Forbidden } from "components/Forbidden";
import { useCurrentOnboardingStep } from "containers/Onboarding/utils";
import {
  GET_ENABLED_FEATURES,
  GET_ORGANISATIONS,
  TOrganisationDetails,
  useGetEnabledFeaturesQuery,
  useGetOrganisationsQuery,
} from "graphql/organisation";
import { TNumberMin } from "graphqlQueries/number";
import { useGlobalContext } from "hooks";
import { usePaymentContext } from "hooks/usePaymentContext";
import { useSelectionContext } from "hooks/useSelectionContext";
import { FullPageLoader } from "lib/loaders/FullPageLoader";
import { FullToast } from "lib/notification/FullToast";
import { Toast } from "lib/notification/Toast";
import { ToastContainer } from "lib/notification/ToastContainer";
import { sortNumbers } from "selectors/number";
import {
  findOrganisation,
  getOrganisationByNumber,
  getOrganisationByUuid,
  getRole,
  isAdmin,
  isOrganisationNumber,
} from "selectors/organisation";
import { UUID_REGEX } from "utils/constants";
import { PostHogPageView } from "utils/context/posthog/PostHogPageView";
import { TurnPostHogProvider } from "utils/context/posthog/PostHogProvider";
import { usePostHogIdentify } from "utils/context/posthog/usePostHogIdentify";
import { initFeatureFlags, resetFeatureFlags } from "utils/featureFlags";
import { Host } from "utils/general";
import { appComponents } from "utils/lazy";
import { getLastNumberUuid, getLastOrganisationUuid } from "utils/localStorage";
import { getChatPermalinkUuidFromURL, getNumberUuidFromURL } from "utils/url";

import { UserActivityTracker } from "./UserActivityTracker";
import { WelcomeVideoModal } from "./WelcomeVideoModal";

const {
  Accounts,
  BillingContainer,
  ButtonsContainer,
  NumberDetail,
  Onboarding,
  PlaybookDetail,
  PlaybookForm,
  PlaybooksPublic,
  QrView,
  Sandbox,
  StackDetail,
  StackDetailCanvas,
  TemplateContainer,
  Thread,
  ReminderDetail,
} = appComponents;

export const { link: networkStatusLink, useApolloNetworkStatus } = createNetworkStatusNotifier();

interface IAppProps {
  client: ApolloClient<NormalizedCacheObject>;
}
const App = ({ client }: IAppProps) => {
  useVH();
  const { turnHostInfo } = useGlobalContext();

  useEffect(() => {
    if (turnHostInfo.turn_host.includes(Host.QA)) {
      const faviconLink = document.getElementById("favicon") as HTMLLinkElement;
      faviconLink.href = faviconLink.href.replace("favicon.ico", "favicon-qa.ico");
    }
  }, [turnHostInfo.turn_host]);

  return (
    <div className="App">
      <ApolloProvider client={client}>
        <UserActivityTracker />
        <Toast />
        <ToastContainer />
        <FullToast />
        <BaseRouter />
      </ApolloProvider>
    </div>
  );
};

export const BaseRouter = () => {
  const { t } = useTranslation();
  const { disabledSubscription } = usePaymentContext();
  const {
    UNSAFE_organisationUuid,
    UNSAFE_numberUuid,
    setNumberUuid,
    setOrganisationUuid,
    logOut,
    setIsAdmin,
    setRole,
    setMyAccountUuid,
    numberUuid,
    currentOrganisation,
    setOnboardingStep,
    setCurrentOrganisation,
  } = useGlobalContext();

  /**
   * Load organisations and set the number/organisation
   */
  const { data, loading, error } = useGetOrganisationsQuery();
  const hasRunOrgsQueryRef = useRef(false);

  useEffect(() => {
    if (!data?.organisations || loading || error) return;
    if (hasRunOrgsQueryRef.current) return; // prevent running more than once
    hasRunOrgsQueryRef.current = true;

    // get deeplink number uuid and organisation if present
    const currentURLNumberUuid = getNumberUuidFromURL(location.href);
    const organisationForCurrentURLNumberUuid = currentURLNumberUuid
      ? getOrganisationByNumber(data?.organisations as TOrganisationDetails[], currentURLNumberUuid)
      : null;

    // get last organisation uuid from local storage
    const lastOrganisationUuid = getLastOrganisationUuid();

    // use organisation for deeplink number uuid, or last organisation uuid
    // or fall back to the first organisation in the organisations list
    const initOrganisation =
      organisationForCurrentURLNumberUuid ||
      getOrganisationByUuid(data?.organisations as TOrganisationDetails[], lastOrganisationUuid);
    const initOrganisationUuid = initOrganisation?.uuid;

    if (initOrganisationUuid) {
      const lastNumberUuid = getLastNumberUuid(initOrganisationUuid);
      const numberUuid = currentURLNumberUuid || lastNumberUuid;
      const numberIsValid = isOrganisationNumber(initOrganisation, numberUuid);
      const initNumberUuid = numberIsValid ? numberUuid : sortNumbers(initOrganisation.numbers)?.[0]?.uuid;

      setCurrentOrganisation(initOrganisation);
      setOrganisationUuid(initOrganisationUuid);
      setNumberUuid(initNumberUuid);

      // For urls that have been stored in the past in external systems
      // we need to keep supporting /c/:chatUuid.
      // Redirecting here to the inbox with the found number
      const chatUuid = getChatPermalinkUuidFromURL(location.href);
      if (chatUuid) {
        location.href = `/app/number/${initNumberUuid}/inbox?chatUuid=${chatUuid}`;
      }
    }
  }, [data, setNumberUuid, setOrganisationUuid, error, loading, setCurrentOrganisation]);

  const organisations = useMemo(() => GET_ORGANISATIONS.parse(data)?.organisations, [data]);
  const posthog = usePostHog();

  useGetEnabledFeaturesQuery({
    variables: {
      numberUuid: UNSAFE_numberUuid as string,
    },
    skip: !UNSAFE_organisationUuid,
    onCompleted: (data) => {
      const { getEnabledFeatures } = GET_ENABLED_FEATURES.parse(data) ?? {};
      resetFeatureFlags();
      initFeatureFlags(getEnabledFeatures);
    },
  });

  useEffect(() => {
    const organisation = findOrganisation(organisations, UNSAFE_organisationUuid);
    const myAccountUuid = organisation?.myAccount.uuid;

    setIsAdmin(isAdmin(organisation));
    setRole(getRole(organisation));
    if (myAccountUuid) {
      setMyAccountUuid(myAccountUuid);
    }
  }, [organisations, UNSAFE_organisationUuid, setIsAdmin, setRole, setMyAccountUuid]);

  // PostHog identify org and number
  usePostHogIdentify({
    organisationUuid: UNSAFE_organisationUuid,
    numberUuid: UNSAFE_numberUuid,
    organisations,
    loading,
    posthog,
  });

  // context(puvvl, 2024-02-22) Init profitwell engagement
  useEffect(() => {
    const organisation = findOrganisation(organisations, UNSAFE_organisationUuid);
    const myAccountEmail = organisation?.myAccount.email;
    if (myAccountEmail) {
      window?.profitwell?.("start", { user_email: myAccountEmail });
    }
    // context(puvvl, 2024-02-22) Need setup it once when we have org id
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [UNSAFE_organisationUuid]);

  const onboardingStep = useCurrentOnboardingStep({
    organisation: currentOrganisation,
    numberUuid,
  });

  useEffect(() => {
    setOnboardingStep(onboardingStep);
  }, [onboardingStep, setOnboardingStep]);

  return loading || !organisations || !UNSAFE_organisationUuid ? (
    <FullPageLoader title={t("loaders.fetching-data")} />
  ) : (
    // context(alexandrchebotar, 2023-05-22): all routes (including children components) can safety use not-null `numberUuid` from `GlobalContext`
    <TurnPostHogProvider>
      <Router basename={process.env.PUBLIC_URL}>
        <QueryParamProvider ReactRouterRoute={Route}>
          <PagesSettings numbers={findOrganisation(organisations, UNSAFE_organisationUuid)?.numbers} />

          <Suspense fallback={<FullPageLoader title={t("loaders.loading-scripts")} />}>
            <WelcomeVideoModal />
            <ConnectionLost />
            <RedirectingRouter organisations={organisations} />
            {/* context(alexandrchebotar, 2023-05-23): utility route for manual logout */}
            <Route
              exact
              path="/logout"
              render={() => {
                logOut();
                return null;
              }}
            />

            <Switch>
              {/* context(alexandrchebotar, 2023-05-22): routes before checking `numberUuid`  should use `UNSAFE_nimberUuid` (including children components) */}

              <Redirect exact from="/sandbox/new" to={`/org/${UNSAFE_organisationUuid}/sandbox/new`} />
              <Route path="/org/:organisationUuid/sandbox/new">
                <Sandbox organisations={organisations} />
              </Route>
              <Route exact path="/onboarding/:organisationUuid?">
                <Onboarding organisations={organisations} />
              </Route>
              <Route exact path="/org/:organisationUuid/members">
                <Accounts organisations={organisations} organisation={null} />
              </Route>

              {/* context(@Puvvl, 2024-04-30): Hide this route until we need it to show */}
              {/* <Route exact path="/payment/:numberUuid">
              <Payment />
            </Route> */}

              {/* context(alexandrchebotar, 2022-03-30): keeping redirections for deprecated routes: "/signup", "/signup-india-bootcamp-2021" */}
              <Redirect exact from="/signup" to="/" />
              {/* todo(alexandrchebotar, 2023-05-23): consider to remove deprecated "/signup-india-bootcamp-2021" route from redirections */}
              <Redirect exact from="/signup-india-bootcamp-2021" to="/" />

              {/* // todo(alexandrchebotar, 2023-05-23): ? add common `numberUuid` setter for all routes with `numberUuid` or `organisationUuid`
                        parameter to not render those components with wrong UUIDs? */}
              {UNSAFE_numberUuid ? (
                // context(alexandrchebotar, 2023-05-22): these routes (including children components) can safety to use not-null `numberUuid` from `GlobalContext`
                <Switch>
                  {/* // context(alexandrchebotar, 2023-10-12): redirect from root to Inbox if feature enabled */}
                  <Redirect exact from="/" to={`/number/${UNSAFE_numberUuid}/inbox`} />
                  {/* Journey routes must come before the catch-all /number/:numberUuid route */}
                  {/* UUID regex ensures these don't match static paths like /journeys/logs, /journeys/data */}
                  <Route exact path={`/number/:numberUuid/journeys/:stackContainerUuid(${UUID_REGEX.source})/canvas`}>
                    <StackDetailCanvas />
                  </Route>
                  <Route
                    exact
                    path={`/number/:numberUuid/journeys/:stackContainerUuid(${UUID_REGEX.source})/canvas/insights`}
                  >
                    <StackDetailCanvas insightsMode />
                  </Route>
                  <Route exact path={`/number/:numberUuid/journeys/:stackContainerUuid(${UUID_REGEX.source})`}>
                    <StackDetail organisations={organisations} />
                  </Route>
                  <Route path="/number/:numberUuid">
                    <NumberDetail organisations={organisations} />
                  </Route>
                  <Route path="/qr/:numberUuid">
                    <QrView />
                  </Route>
                  <Redirect exact from="/billing" to={`/billing/${UNSAFE_numberUuid}`} />
                  <Route path="/billing/:numberUuid">
                    <BillingContainer organisations={organisations} />
                  </Route>
                  <Route exact path="/template/:numberUuid/:templateUuid?">
                    <TemplateContainer />
                  </Route>
                  <Route exact path={["/threads/:numberUuid/new", "/threads/:numberUuid/edit/:threadUuid"]}>
                    <Thread organisations={organisations} />
                  </Route>
                  <Route exact path="/buttons/:numberUuid/:cardUuid?">
                    <ButtonsContainer />
                  </Route>
                  <Route
                    exact
                    path={["/playbooks/:numberUuid/new/:threadUuid?", "/playbooks/:numberUuid/edit/:editPlaybookUuid"]}
                  >
                    <PlaybookForm organisations={organisations} />
                  </Route>
                  <Route exact path="/playbooks/public">
                    <PlaybooksPublic />
                  </Route>
                  <Route exact path={["/playbooks/:playbookUuid", "/playbooks/public/:playbookUuid"]}>
                    <PlaybookDetail />
                  </Route>
                  <Route exact path="/stacks/:numberUuid">
                    <StackDetail organisations={organisations} />
                  </Route>
                  {/* Backward compatibility redirects for old stack/canvas routes */}
                  <Route
                    exact
                    path="/stacks/:numberUuid/:stackContainerUuid/canvas/insights"
                    render={({ match }) => (
                      <Redirect
                        to={`/number/${match.params.numberUuid}/journeys/${match.params.stackContainerUuid}/canvas/insights`}
                      />
                    )}
                  />
                  <Route
                    exact
                    path="/stacks/:numberUuid/:stackContainerUuid/canvas"
                    render={({ match }) => (
                      <Redirect
                        to={`/number/${match.params.numberUuid}/journeys/${match.params.stackContainerUuid}/canvas`}
                      />
                    )}
                  />
                  <Route
                    exact
                    path="/stacks/:numberUuid/:stackContainerUuid"
                    render={({ match }) => (
                      <Redirect to={`/number/${match.params.numberUuid}/journeys/${match.params.stackContainerUuid}`} />
                    )}
                  />
                  {!disabledSubscription && (
                    <Route exact path="/reminder/:numberUuid/">
                      <ReminderDetail organisations={organisations} />
                    </Route>
                  )}
                </Switch>
              ) : (
                <Redirect exact path="/" to={`/onboarding/${UNSAFE_organisationUuid}`} />
              )}
            </Switch>
          </Suspense>
          <PostHogPageView />
        </QueryParamProvider>
      </Router>
    </TurnPostHogProvider>
  );
};

interface IRedirectingRouter {
  organisations: TOrganisationDetails[];
}
const RedirectingRouter = ({ organisations }: IRedirectingRouter) => {
  const { organisationUuid } = useGlobalContext();
  const { queryError, mutationError } = useApolloNetworkStatus();
  const history = useHistory();

  // context(alexandrchebotar, 2021-10-25): Check if the user enter non-existing organisation uuid in URL
  // or if the used doesn't have access to that organisation. We need it because we fetch not a single organisation
  // but all allowed organisations
  // TODO(alexandrchebotar, 2021-10-25): figure out why <Redirect to="/forbidden" /> doesn't work in this case
  const organisationNotFound = !findOrganisation(organisations, organisationUuid);
  useEffect(() => {
    if (organisationNotFound) {
      history.replace("/forbidden");
    }
  }, [history, organisationNotFound]);

  const isPresentNotFoundError = () => {
    if (queryError?.response?.errors?.find(({ code }: any) => code === 403)) {
      return true;
    } else if (mutationError?.response?.errors?.find(({ code }: any) => code === 403)) {
      return true;
    }
    return false;
  };

  return (
    <>
      {isPresentNotFoundError() && <Redirect to="/forbidden" />}
      <Route exact path="/forbidden" render={() => <Forbidden />} />
    </>
  );
};

const PagesSettings = ({ numbers }: { numbers?: TNumberMin[] }) => {
  const { pathname } = useLocation();
  const { setSelectedItems } = useSelectionContext();

  useEffect(() => {
    if (pathname) {
      setSelectedItems(undefined);
    }
  }, [pathname, setSelectedItems]);

  return (
    <>
      <ConvertToAppModal />
      <DeleteJourneyModal />
      <DuplicateJourneyModal numbers={numbers} />
    </>
  );
};

export default App;
